import React, { useState } from "react";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Typography
} from "@mui/material";
import { makeValidate, makeRequired, showErrorOnBlur } from "mui-rff";
import { Form } from "react-final-form";
import PasswordChecklist from "react-password-checklist";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "i18next";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  PageBox,
  PageAlert,
  PageHeader,
  FormButton,
  FormErrorHelperText,
  FormTextField
} from "./ResetPassword.style";
import { ResetPasswordSchema } from "../../schema/ResetPasswordSchema";
import { ResetPasswordInterface } from "../../interfaces/ResetPasswordInterface";
import { useAuth } from "../../providers/Auth";
import { TabTitle } from "../../utils/GeneralFunctions";
import { AlertStateInterface } from "../../interfaces/AlertStateInterface";

/**
 * Uses the optional helper makeValidate function to format the error messages
 * into something usable by final form.
 */
const validate = makeValidate(ResetPasswordSchema, error => {
  return <span className="error">{i18n.t(error.message)}</span>;
});

/**
 * Grabs all the required fields from the schema so that they can be passed into
 * the components without having to declare them in both the schema and the component.
 */
const required = makeRequired(ResetPasswordSchema);

const ResetPassword = () => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as AlertStateInterface;
  TabTitle(t("resetPassword.block1.header.page-title"));
  const { resetPassword } = useAuth();

  const [submittedValues, setSubmittedValues] = useState<
    ResetPasswordInterface | undefined
  >(undefined);

  const initialValues: ResetPasswordInterface = {
    newPassword: "",
    confirmPassword: ""
  };

  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false
  });

  const onSubmit = async (values: ResetPasswordInterface) => {
    setSubmittedValues(values);

    const resetResult: any = await resetPassword(values.newPassword, values.confirmPassword);

    if (resetResult.code === 1005) {
      navigate("/reset-password", {
        state: {
          showAlert: true,
          severity: "error",
          message: resetResult.message
        }
      });
    } else if (resetResult.code === 1000) {
      navigate("/login", {
        state: {
          showAlert: true,
          severity: "success",
          message: "resetPassword.block-form.success"
        }
      });
    }
  };

  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  return (
    <>
    {state && state.showAlert && (
      <PageAlert
        severity={
          state.severity === "success"
            ? "success"
            : state.severity === "error"
            ? "error"
            : state.severity === "info"
            ? "info"
            : "warning"
        }
      >
        {t(state.message)}
      </PageAlert>
   )}
    <PageBox>
      <Grid container sx={{ pt: 0 }}>
        <Grid item xs={12} sm={6}>
          <PageHeader>
            {t("resetPassword.block1.header.page-title")}
          </PageHeader>
          <Typography component="p" variant="lead">
            {t("resetPassword.block1.header.short-intro")}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Form
            onSubmit={onSubmit}
            initialValues={submittedValues ? submittedValues : initialValues}
            validate={validate}
            render={({ submitError, handleSubmit, submitting, values }) => (
              <form
                onSubmit={handleSubmit}
                noValidate={true}
                autoComplete="new-password"
              >
                <FormTextField
                  required={required.newPassword}
                  type={
                    showPassword && showPassword.newPassword
                      ? "text"
                      : "password"
                  }
                  label={t("resetPassword.block-form.input.newPassword")}
                  id="newPassword"
                  name="newPassword"
                  variant="filled"
                  showError={() => {
                    return;
                  }}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword && showPassword.newPassword
                              ? t("all.button.password-icon-show")
                              : t("all.button.password-icon-hide")
                          }
                          onClick={() =>
                            setShowPassword(prevState => ({
                              ...prevState,
                              newPassword: !prevState.newPassword
                            }))
                          }
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          component="button"
                        >
                          {showPassword && showPassword.newPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Typography
                  component="p"
                  variant="h4"
                  sx={{ mb: theme => theme.spacer_n }}
                >
                  {t("resetPassword.block-form.text.newPassword")}:
                </Typography>
                <Box sx={{ mb: theme => theme.spacer_n }}>
                  <PasswordChecklist
                    rules={[
                      "minLength",
                      "specialChar",
                      "number",
                      "capital",
                      "lowercase",
                      "match"
                    ]}
                    minLength={10}
                    value={
                      values && values.newPassword ? values.newPassword : ""
                    }
                    valueAgain={
                      values && values.confirmPassword
                        ? values.confirmPassword
                        : ""
                    }
                    messages={{
                      minLength: t(
                        "resetPassword.block-form.error.password-characters-minimum"
                      ),
                      specialChar: t(
                        "resetPassword.block-form.error.password-special-character-minimum"
                      ),
                      number: t(
                        "resetPassword.block-form.error.password-number-minimum"
                      ),
                      capital: t(
                        "resetPassword.block-form.error.password-uppercase-minimum"
                      ),
                      lowercase: t(
                        "resetPassword.block-form.error.password-lowercase-minimum"
                      ),
                      match: t(
                        "resetPassword.block-form.error.password-match"
                      ),
                    }}
                  />
                </Box>
                <FormTextField
                  required={required.confirmPassword}
                  type={
                    showPassword && showPassword.confirmPassword
                      ? "text"
                      : "password"
                  }
                  label={t("resetPassword.block-form.input.confirmPassword")}
                  id="confirmPassword"
                  name="confirmPassword"
                  variant="filled"
                  showError={showErrorOnBlur}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword && showPassword.confirmPassword
                              ? t("all.button.password-icon-show")
                              : t("all.button.password-icon-hide")
                          }
                          onClick={() =>
                            setShowPassword(prevState => ({
                              ...prevState,
                              confirmPassword: !prevState.confirmPassword
                            }))
                          }
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          component="button"
                        >
                          {showPassword && showPassword.confirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                {submitError && (
                  <FormErrorHelperText
                    variant="filled"
                    filled
                    required
                    component="p"
                  >
                    <span className="error">{t(submitError)}</span>
                  </FormErrorHelperText>
                )}
                <FormButton
                  variant="contained"
                  type="submit"
                  disabled={submitting}
                  size="large"
                  sx={{
                    mr: theme => theme.spacer_m,
                    mb: theme => theme.spacer_m
                  }}
                  disableElevation
                >
                  {t("resetPassword.block-form.button.submit")}
                </FormButton>
              </form>
            )}
          />
        </Grid>
      </Grid>
    </PageBox>
    </>
  );
};

export default ResetPassword;
